//@ts-ignore
import { AlertDialog, AlertDialogContent } from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import StepCommon from "@/components/walk-through/explore/StepCommon";
import { GetStaticProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Layout from "../components/Layouts/Layout";
import Meta from "../components/Layouts/Meta";
import Section1 from "../sections/build/section-1";
import { useUserContext } from "@/contexts/userContext";
import {
  useGetUserWalkthroughStatus,
  usePostSetUserWalkthroughStatus,
} from "@/api/userApi";
import { usePostCreateStrategy } from "@/api/strategyApi";
import { useRecoilState } from "recoil";
import { brokerConnectModalAtom } from "@/Atoms/modal/DialogueAtoms";
import { CURRENT_DEFAULT_SYMBOL } from "@/config/logos";

const BuildPage: any = () => {
  const { user } = useUserContext();
  const router = useRouter();
  const { t } = useTranslation("build");
  const [userLoading, setUserLoading] = useState<any>(true);
  const [newUserSteps, setNewUserSteps] = useState(0);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom
  );

  const {
    data: userWalkthroughData, // Stores the fetched walkthrough data
    isSuccess: isUserWalkthroughSuccess, // Indicates if the query was successful
    isLoading: isWalkthroughLoading, // Indicates if the query is currently loading
  } = useGetUserWalkthroughStatus(user?.user_id); // Fetch walkthrough status only if user ID exists
  const { mutateAsync: updateUserWalkthroughStatus } =
    usePostSetUserWalkthroughStatus(); // Fetch walkthrough status only if user ID exists

  const {
    data: createStrategyData,
    mutateAsync: createStrategy,
    isSuccess: isCreateStrategySuccess, // Indicates if the query was successful
    isPending: isCreateStrategyLoading, // Indicates if the query is currently loading
  } = usePostCreateStrategy(); // Fetch walkthrough status only if user ID exists

  const handleUpdateUserWalkthrough = async (
    step: number,
    closeModal: boolean = false
  ) => {
    await updateUserWalkthroughStatus({
      step: step.toString(),
      user_id: user?.user_id,
    });
    if (step == 4) handelCreateStartegy(CURRENT_DEFAULT_SYMBOL);
    if (!closeModal) setNewUserOpen(true);
    setNewUserSteps(step < 4 ? ++step : step);
    setLoading(false);
  };
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user-info")!);
    user?.user_id && setUserLoading(false);
    if (!user?.user_id) {
      router.push({
        pathname: "/",
        query: {
          redirect: window.location.href,
          locale: router.query.locale || "en",
        },
      });
      return;
    }
  }, []);

  const handelCreateStartegy = async (symbol: string) => {
    setLoading(true);
    await createStrategy({
      symbol: symbol,
      timeframe: "15m",
      strategy_name: symbol + " - Strategy",
      user_id: user?.user_id ?? "",
    });
    setLoading(false);
  };

  useEffect(() => {
    // Check if the user walkthrough data has successfully loaded
    if (isCreateStrategySuccess && createStrategyData) {
      var should_show_nudges = true;
      if (createStrategyData.data.meta.nudges_tracker == "1")
        should_show_nudges = false;
      router.push({
        pathname:
          "/strategy/" +
          createStrategyData.data.strategy.id +
          "/" +
          createStrategyData.data.strategy.keygen +
          "/" +
          should_show_nudges,
        query: {
          ref: router.asPath,
          walkthrough: should_show_nudges,
          locale: router.query.locale || "en",
        },
      });
    }
  }, [isCreateStrategyLoading, isCreateStrategySuccess]); // Runs when either isUserWalkthroughSuccess or userWalkthroughData changes

  useEffect(() => {
    // Check if the user walkthrough data has successfully loaded
    if (isUserWalkthroughSuccess && userWalkthroughData) {
      setNewUserOpen(true);
      // Extract walkthrough status, defaulting to 0 if it's undefined or null
      // Increment it by 1 and update the new user steps
      setNewUserSteps(
        (+userWalkthroughData?.data?.walkthrough_status || 0) + 1
      );
    }
  }, [isUserWalkthroughSuccess, userWalkthroughData]); // Runs when either isUserWalkthroughSuccess or userWalkthroughData changes

  const main = () => (
    <>
      <div className={" mx-auto "}>
        <Section1 userInfo={user} />
      </div>
      <AlertDialog
        open={
          !showConnectForm &&
          newUserOpen &&
          [1, 2, 3, 4].includes(+newUserSteps)
        }
      >
        <AlertDialogContent className="">
          <div>
            <ScrollArea className=" p-0 ">
              <StepCommon
                loading={loading || isWalkthroughLoading}
                closeButtonClick={handleUpdateUserWalkthrough}
                nextBtnClick={handleUpdateUserWalkthrough}
              />
            </ScrollArea>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return (
    <Layout
      meta={<Meta title={t("meta.title")} description={t("meta.title")} />}
    >
      {main()}
    </Layout>
  );
};
export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || "en", [
        "build",
        "market-screener",
        "layout",
        "common",
        "test",
        "trade",
        "walk-through",
        "alert-strategy",
        "add-ons-form",
      ])),
    },
    revalidate: 60, // Update translations every 60 seconds (ISR)
  };
};
export default BuildPage;
